import React, { useState, useRef, useCallback, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import './Passwords.css';

const Passwords = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const lastSubmissionRef = useRef(null);
  const formRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const abortControllerRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name] || errors.general) {
      setErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[name]) {
          delete newErrors[name];
        }
        if (newErrors.general) {
          delete newErrors.general;
        }
        return newErrors;
      });
    }
  };

  const togglePasswordVisibility = (fieldName) => {
    setShowPasswords(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = ['Current password is required'];
    }

    if (!formData.newPassword.trim()) {
      newErrors.newPassword = ['New password is required'];
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = ['New password must be at least 8 characters long'];
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = ['Please confirm your new password'];
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = ['Passwords do not match'];
    }

    // Only check if current password is different if both fields have values
    if (formData.currentPassword.trim() && formData.newPassword.trim() && formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = ['New password must be different from current password'];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Always allow client-side validation to run
    if (!validateForm()) {
      return;
    }
    
    // Only prevent duplicate submissions for actual API calls
    if (isSubmittingRef.current || isLoading) {
      console.log('Submission blocked - already in progress');
      return;
    }
    
    // Debounce: prevent submissions within 2 seconds of each other (reduced from 3)
    const now = Date.now();
    if (lastSubmissionRef.current && (now - lastSubmissionRef.current) < 2000) {
      console.log('Submission blocked - too soon after last submission');
      return;
    }

    // Set submission flags
    isSubmittingRef.current = true;
    lastSubmissionRef.current = now;
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');
    
    // Create abort controller for request cancellation
    abortControllerRef.current = new AbortController();
    
    // Disable form during submission
    if (formRef.current) {
      formRef.current.style.pointerEvents = 'none';
      formRef.current.style.opacity = '0.7';
    }

    try {
      const response = await fetch(getApiUrl('/profile/update-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
          new_password_confirmation: formData.confirmPassword
        }),
        signal: abortControllerRef.current.signal
      });

      // Handle 204 No Content response - but don't block it completely
      if (response.status === 204) {
        console.warn('Received 204 No Content - likely duplicate request');
        setIsLoading(false);
        isSubmittingRef.current = false;
        // Re-enable form
        if (formRef.current) {
          formRef.current.style.pointerEvents = 'auto';
          formRef.current.style.opacity = '1';
        }
        return;
      }

      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Invalid response format');
      }

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(data.message || 'Password updated successfully!');
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setShowPasswords({
          currentPassword: false,
          newPassword: false,
          confirmPassword: false
        });
      } else {
        // Reset submission flag for validation errors so user can try again immediately
        isSubmittingRef.current = false;
        
        if (data.errors) {
          // Convert backend errors to user-friendly messages
          const userFriendlyErrors = {};
          
          // Map backend field names to frontend field names
          const fieldMapping = {
            'current_password': 'currentPassword',
            'new_password': 'newPassword',
            'new_password_confirmation': 'confirmPassword'
          };
          
          Object.keys(data.errors).forEach(key => {
            const errorMessages = data.errors[key];
            // Map backend field name to frontend field name
            const frontendFieldName = fieldMapping[key] || key;
            
            if (Array.isArray(errorMessages)) {
              userFriendlyErrors[frontendFieldName] = errorMessages.map(msg => {
                // Make error messages more user-friendly
                if (msg.includes('Current password is incorrect')) {
                  return 'The current password you entered is incorrect. Please check and try again.';
                } else if (msg.includes('New password must be different')) {
                  return 'Your new password must be different from your current password.';
                } else if (msg.includes('must be at least 8 characters')) {
                  return 'Your new password must be at least 8 characters long.';
                } else if (msg.includes('confirmation does not match')) {
                  return 'The password confirmation does not match your new password.';
                }
                return msg;
              });
            } else {
              userFriendlyErrors[frontendFieldName] = errorMessages;
            }
          });
          
          // Debug: Log the error mapping
          console.log('Backend errors:', data.errors);
          console.log('Mapped frontend errors:', userFriendlyErrors);
          
          setErrors(userFriendlyErrors);
        } else {
          // Handle general error messages
          let friendlyMessage = data.message || 'Failed to update password. Please try again.';
          if (friendlyMessage.includes('Current password is incorrect')) {
            friendlyMessage = 'The current password you entered is incorrect. Please check and try again.';
          } else if (friendlyMessage.includes('must be different')) {
            friendlyMessage = 'Your new password must be different from your current password.';
          }
          setErrors({ general: friendlyMessage });
        }
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error.name === 'AbortError') {
        console.log('Request was aborted');
        return;
      }
      console.error('Password update error:', error);
      setErrors({ general: 'Network error. Please check your connection and try again.' });
    } finally {
      // Reset submission flags (only if not already reset by validation errors)
      if (isSubmittingRef.current !== false) {
        isSubmittingRef.current = false;
      }
      setIsLoading(false);
      abortControllerRef.current = null;
      
      // Re-enable form
      if (formRef.current) {
        formRef.current.style.pointerEvents = 'auto';
        formRef.current.style.opacity = '1';
      }
    }
  }, [formData, isLoading, errors]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Debug: Log errors state whenever it changes
  useEffect(() => {
    console.log('Errors state updated:', errors);
  }, [errors]);


  return (
    <div className="passwords-container">
      <div className="passwords-header">
        <h2>Password Settings</h2>
        <p>Update your password to keep your account secure</p>
      </div>

      <div className="passwords-content">
        <div className="passwords-layout">
          <div className="passwords-form-section">
            {successMessage && (
              <div className="success-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
                {successMessage}
              </div>
            )}

            {errors.general && (
              <div className="error-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <div>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>{errors.general}</div>
                  <div style={{ fontSize: '12px', opacity: '0.8' }}>
                    Please check the details above and try again. If you continue to have issues, contact support.
                  </div>
                </div>
              </div>
            )}

            <form 
              ref={formRef} 
              onSubmit={handleSubmit} 
              className="passwords-form"
              onDoubleClick={(e) => e.preventDefault()}
            >
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.currentPassword ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                className={errors.currentPassword && errors.currentPassword.length > 0 ? 'error' : ''}
                placeholder="Enter your current password"
                disabled={isLoading}
                readOnly={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('currentPassword')}
                disabled={isLoading}
              >
                {showPasswords.currentPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            {errors.currentPassword && errors.currentPassword.length > 0 && (
              <div>
                <span className="error-text">{errors.currentPassword[0]}</span>
                <div className="password-help-text">
                  💡 <strong>Tip:</strong> Make sure Caps Lock is off and check for any typos. If you've forgotten your password, you may need to reset it.
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.newPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                className={errors.newPassword && errors.newPassword.length > 0 ? 'error' : ''}
                placeholder="Enter your new password"
                disabled={isLoading}
                readOnly={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('newPassword')}
                disabled={isLoading}
              >
                {showPasswords.newPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            {errors.newPassword && errors.newPassword.length > 0 && <span className="error-text">{errors.newPassword[0]}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPasswords.confirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword && errors.confirmPassword.length > 0 ? 'error' : ''}
                placeholder="Confirm your new password"
                disabled={isLoading}
                readOnly={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => togglePasswordVisibility('confirmPassword')}
                disabled={isLoading}
              >
                {showPasswords.confirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            {errors.confirmPassword && errors.confirmPassword.length > 0 && <span className="error-text">{errors.confirmPassword[0]}</span>}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || isSubmittingRef.current}
              style={{ 
                opacity: (isLoading || isSubmittingRef.current) ? 0.7 : 1,
                cursor: (isLoading || isSubmittingRef.current) ? 'not-allowed' : 'pointer',
                pointerEvents: (isLoading || isSubmittingRef.current) ? 'none' : 'auto'
              }}
              onDoubleClick={(e) => e.preventDefault()}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Updating Password...
                </>
              ) : (
                'Update Password'
              )}
            </button>
          </div>
        </form>
          </div>

          <div className="passwords-help-section">
            <div className="help-card">
              <div className="help-header">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <h3>Password Requirements</h3>
              </div>
              <div className="help-content">
                <div className="requirement-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12"></polyline>
                  </svg>
                  <span>At least 8 characters long</span>
                </div>
                <div className="requirement-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12"></polyline>
                  </svg>
                  <span>Must be different from current password</span>
                </div>
                <div className="requirement-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12"></polyline>
                  </svg>
                  <span>Confirm new password matches</span>
                </div>
              </div>
            </div>


            <div className="help-card">
              <div className="help-header">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"></path>
                  <path d="M12 6v6l4 2"></path>
                </svg>
                <h3>Need Help?</h3>
              </div>
              <div className="help-content">
                <div className="help-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span>Contact support if you're having issues</span>
                </div>
                <div className="help-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  <span>Use a password manager for better security</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Passwords;
